'use client';
import { useCallback, useEffect, useState } from "react";
import BlogCard from "../ui/BlogCard";
import Button from "../ui/Button";
import EbooksCard from "../ui/EbooksCard";
import HomeBlogCardHero from "../ui/HomeBlogCardHero";
import SectionTitle from "../ui/SectionTitle";

const tabs = ["Latest Blogs", "News & PR", "Thought Leadership"]; // "Whitepapers / eBooks"

interface BlogData {
    _id: string;
    title: string;
    slug: string;
    excerpt: string;
    featuredImage?: string;
    tags: string[];
    publishDate?: string;
    formattedDate: string;
    author?: string;
    blogType: 'Post' | 'News';
    postType?: 'News' | 'Press Release';
}

interface ApiBlogData {
    _id: string;
    title: string;
    slug: string;
    excerpt: string;
    featuredImage?: string;
    tags?: string[];
    publishDate?: string;
    author?: string;
    blogType: 'Post' | 'News';
    postType?: 'News' | 'Press Release';
}

interface TabContent {
    hero: BlogData | null;
    cards: BlogData[];
}

const staticTabData: Record<string, TabContent> = {
    "Latest Blogs": {
        hero: null,
        cards: [],
    },
    "Whitepapers / eBooks": {
        hero: null,
        cards: [],
    },
    "Thought Leadership": {
        hero: null,
        cards: [],
    },
    "News & PR": {
        hero: null,
        cards: [],
    }
};

// Helper function to format dates
const formatDate = (dateString?: string): string => {
    if (!dateString) return new Date().toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};

// Helper function to get primary category from tags
// Returns empty string if no valid category found (so badge won't show)
const getPrimaryCategory = (tags?: string[] | null): string => {
    if (!tags || tags.length === 0) return '';
    const firstTag = tags[0]?.trim();
    // Return empty string for generic/fallback values so badge won't show
    if (!firstTag || firstTag === '' ||
        firstTag.toLowerCase() === 'blog' ||
        firstTag.toLowerCase() === 'general' ||
        firstTag.toLowerCase() === 'blog post') {
        return '';
    }
    return firstTag;
};

const ResourcesPage = () => {
    const [activeTab, setActiveTab] = useState<string>("Latest Blogs");
    const [tabData, setTabData] = useState<Record<string, TabContent>>(staticTabData);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isXL, setIsXL] = useState<boolean>(false);

    // Check if screen is XL or above (1280px)
    useEffect(() => {
        const checkScreenSize = () => {
            setIsXL(window.innerWidth >= 1280);
        };

        // Check on mount
        checkScreenSize();

        // Add event listener
        window.addEventListener('resize', checkScreenSize);

        // Cleanup
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Fetch blog data
    const fetchBlogData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/blogs?limit=5&sortBy=createdAt&sortOrder=desc');
            if (!response.ok) {
                throw new Error('Failed to fetch blog data');
            }

            const data = await response.json();
            const blogs: BlogData[] = data.blogs.map((blog: ApiBlogData) => ({
                _id: blog._id,
                title: blog.title,
                slug: blog.slug,
                excerpt: blog.excerpt,
                featuredImage: blog.featuredImage,
                tags: blog.tags || [],
                publishDate: blog.publishDate,
                formattedDate: formatDate(blog.publishDate),
                author: blog.author,
                blogType: blog.blogType
            }));

            // Always show 5 blogs: latest as hero, other 4 as cards
            const hero = blogs.length > 0 ? blogs[0] : null; // Latest blog (by createdAt)
            const cards = blogs.slice(1, 5); // Next 4 recent blogs

            setTabData(prev => ({
                ...prev,
                "Latest Blogs": {
                    hero,
                    cards
                }
            }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error fetching blog data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch News & PR data
    const fetchNewsPrData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/news-pr?limit=5&sortBy=publishDate&sortOrder=desc');
            if (!response.ok) {
                throw new Error('Failed to fetch news & PR data');
            }

            const data = await response.json();
            const newsPr: BlogData[] = data.newsPr.map((item: ApiBlogData) => ({
                _id: item._id,
                title: item.title,
                slug: item.slug,
                excerpt: item.excerpt,
                featuredImage: item.featuredImage,
                tags: item.tags || [],
                publishDate: item.publishDate,
                formattedDate: formatDate(item.publishDate),
                author: item.author,
                blogType: 'News' as const,
                postType: item.postType || 'News'
            }));

            // Always show 5 items: latest as hero, other 4 as cards
            const hero = newsPr.length > 0 ? newsPr[0] : null;
            const cards = newsPr.slice(1, 5);

            setTabData(prev => ({
                ...prev,
                "News & PR": {
                    hero,
                    cards
                }
            }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error fetching news & PR data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch Thought Leadership data
    const fetchThoughtLeadershipData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/thought-leadership?limit=5&sortBy=publishDate&sortOrder=desc');
            if (!response.ok) {
                throw new Error('Failed to fetch thought leadership data');
            }

            const data = await response.json();
            const thoughtLeadership: BlogData[] = data.thoughtLeadership.map((item: ApiBlogData) => ({
                _id: item._id,
                title: item.title,
                slug: item.slug,
                excerpt: item.excerpt,
                featuredImage: item.featuredImage,
                tags: item.tags || [],
                publishDate: item.publishDate,
                formattedDate: formatDate(item.publishDate),
                author: item.author,
                blogType: 'Post' as const
            }));

            // Always show 5 items: latest as hero, other 4 as cards
            const hero = thoughtLeadership.length > 0 ? thoughtLeadership[0] : null;
            const cards = thoughtLeadership.slice(1, 5);

            setTabData(prev => ({
                ...prev,
                "Thought Leadership": {
                    hero,
                    cards
                }
            }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error fetching thought leadership data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch data based on active tab
    const fetchTabData = useCallback(async (tab: string) => {
        if (tab === "Latest Blogs") {
            await fetchBlogData();
        } else if (tab === "News & PR") {
            await fetchNewsPrData();
        } else if (tab === "Thought Leadership") {
            await fetchThoughtLeadershipData();
        }
    }, [fetchBlogData, fetchNewsPrData, fetchThoughtLeadershipData]);

    // Fetch data on component mount and when active tab changes
    useEffect(() => {
        fetchTabData(activeTab);
    }, [activeTab, fetchTabData]);

    const content = tabData[activeTab];
    const isEbookTab = activeTab === "Whitepapers / eBooks";
    const routeMap: Record<string, string> = {
        "Latest Blogs": "/blogs",
        "Whitepapers / eBooks": "/whitepapers",
        "Thought Leadership": "/thought-leadership",
        "News & PR": "/news-pr",
    };
    const exploreHref = routeMap[activeTab] || "#";

    // Filter tabs based on screen size - only show "Latest Blogs" below XL
    const visibleTabs = isXL ? tabs : tabs.filter(tab => tab === "Latest Blogs");

    // Reset to "Latest Blogs" if current tab is hidden on smaller screens
    useEffect(() => {
        if (!isXL && activeTab !== "Latest Blogs") {
             
            setActiveTab("Latest Blogs");
        }
    }, [isXL, activeTab]);

    return (
        <section className="  pt-8 sm:pt-12 lg:pt-16 xl:pt-20 relative overflow-hidden text-white ">
            <div className="custom-container overflow-hidden flex flex-col gap-4 lg:gap-8 ">
                {/* Heading */}
                <div className=" flex justify-between md:items-center flex-col md:flex-row">
                    <SectionTitle title="Marketing Intelligence" subtitle="Enterprise" align="left" />
                    <div className="mt-4 overflow-x-auto sm:overflow-x-visible whitespace-nowrap px-2">
                        <div className="inline-flex space-x-8">
                            {visibleTabs.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`inline-block pb-1 text-sm md:text-base font-medium hover:cursor-pointer ${activeTab === tab
                                        ? "border-b-2 border-[#E0301E] text-[#E0301E] font-semibold"
                                        : "text-black/80 hover:text-[#E0301E]"
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E0301E]"></div>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="text-center py-20">
                        <p className="text-red-500 mb-4">Error loading content: {error}</p>
                        <Button variant="primary" size="md" onClick={() => fetchTabData(activeTab)}>
                            Try Again
                        </Button>
                    </div>
                )}

                {/* Grid */}
                {!loading && !error && content && (
                    isEbookTab ? (
                        // eBooks: hide hero and show 3x3 EbooksCard grid
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {content.cards.map((card, index) => (
                                <EbooksCard
                                    key={card._id || index}
                                    image={card.featuredImage || "/test-images/blog-img-1.png"}
                                    date={card.formattedDate}
                                    title={card.title}
                                    slug={card.slug}
                                />
                            ))}
                        </div>
                    ) : (
                        // All tabs use the same layout: HomeBlogCardHero and BlogCard with appropriate basePath
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                            {/* Determine basePath based on active tab */}
                            {(() => {
                                const basePathMap: Record<string, string> = {
                                    "Latest Blogs": "/blogs",
                                    "News & PR": "/news-pr",
                                    "Thought Leadership": "/thought-leadership",
                                };
                                const basePath = basePathMap[activeTab] || "/blogs";

                                return (
                                    <>
                                        {/* Row 1 — Hero spans 2 cols on XL, full width below */}
                                        {content.hero && (
                                            <div className="sm:col-span-2 xl:col-span-2">
                                                <HomeBlogCardHero
                                                    image={content.hero.featuredImage || "/test-images/blog-img-1.png"}
                                                    date={content.hero.formattedDate}
                                                    title={content.hero.title}
                                                    excerpt={content.hero.excerpt}
                                                    category={getPrimaryCategory(content.hero.tags)}
                                                    slug={content.hero.slug}
                                                    basePath={basePath}
                                                    {...(content.hero.author && { author: content.hero.author })}
                                                />
                                            </div>
                                        )}

                                        {/* 1st BlogCard — right of Hero on XL, below Hero on smaller */}
                                        {content.cards.length > 0 && (
                                            <div>
                                                <BlogCard
                                                    key={content.cards[0]._id || 0}
                                                    image={content.cards[0].featuredImage || "/test-images/blog-img-1.png"}
                                                    date={content.cards[0].formattedDate}
                                                    category={getPrimaryCategory(content.cards[0].tags)}
                                                    title={content.cards[0].title}
                                                    excerpt={content.cards[0].excerpt}
                                                    slug={content.cards[0].slug}
                                                    basePath={basePath}
                                                />
                                            </div>
                                        )}

                                        {/* Row 2 — next 3 BlogCards (2x2 on md, 3x1 on xl) */}
                                        {content.cards.slice(1, 4).map((card, index) => (
                                            <div key={card._id || index}>
                                                <BlogCard
                                                    image={card.featuredImage || "/test-images/blog-img-1.png"}
                                                    date={card.formattedDate}
                                                    category={getPrimaryCategory(card.tags)}
                                                    title={card.title}
                                                    excerpt={card.excerpt}
                                                    slug={card.slug}
                                                    basePath={basePath}
                                                />
                                            </div>
                                        ))}
                                    </>
                                );
                            })()}
                        </div>
                    )
                )}


                {/* Empty State */}
                {!loading && !error && content && !content.hero && content.cards.length === 0 && (
                    <div className="text-center py-20">
                        <p className="text-gray-500 mb-4">No content available at the moment.</p>
                        <p className="text-gray-400">Check back later for new content!</p>
                    </div>
                )}

                {/* Explore More Button */}
                <div className="text-center ">
                    <Button variant="primary" size="lg" href={exploreHref}>Explore More</Button>
                </div>
            </div>
        </section>
    );
};

export default ResourcesPage;
